import type { BaseEnvelope, BasePatchOp } from './base-patch';
import type { AppliedFrame, AppliedNewEntity, AppliedTouchedRow } from '../transport/applied-frame';
import { fromBaseRecord } from './from-base-context';
import { encounterCreatureField, encounterField, layerOf } from '../../shared/array-types';
import { applyAppliedFrame } from '../transport/apply-applied-frame';
import { compileEncounterRosterTotals } from './from-base-encounter';
import { sameTree } from './tree-transfer';
import { encounterMemberSharedInputs } from './encounter-member-input';
import { absorbInventoryMetadata } from './inventory-shared-catalog';

type Row = Record<string, unknown>;
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const idOf = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
/** The page installing into a held destination already owns that room's shared raw closure (catalog, inventory metadata, random tables,
 *  videos: 5.7 MB per mover on the C5 capture, 10 copies in one 70 MB wire before 2026-10-28). A born mover carries only its own rows
 *  plus the definitions the destination lacks; `install` hydrates the rest from the held destination (hydrateSpawnBirth), exactly as a
 *  spawn birth does. A member facet with no held destination keeps the complete base. */
export function compactRelocationArrival(base: Row, envelope: BaseEnvelope): Row {
    if (envelope.entity !== 'encounter' || !fromBaseRecord(envelope.base)) return base;
    const shared = encounterMemberSharedInputs(base);
    delete shared.inventory_order; // each mover keeps its own order
    const compact: Row = Object.fromEntries(Object.entries(base).filter(([key]) => !(key in shared)));
    // hydrateSpawnBirth re-derives the owner list from the held roster, and the context from the held room itself.
    delete compact.status_owners;
    if (fromBaseRecord(compact.encounter_context) && upper(encounterField(compact.encounter_context, 'encounter_id')) === upper(envelope.entity_id)) delete compact.encounter_context;
    return compact;
}
/** What the movers bring travels ONCE: the part of each arrival's shared-input package (catalog closure, metadata, random tables, videos)
 *  that the held destination does not already carry — the policy-exact inverse of the merge the compiler made — merged into one
 *  `new_entity_base` package that expandAppliedFrameInputs spreads back over every arrival on decode (the spawn birth contract), before the
 *  installer hydrates the held destination's package over it. A member facet's arrivals keep their complete bases and share only their catalog. */
function sharedArrivalInputs(arrivals: readonly AppliedNewEntity[], envelope: BaseEnvelope): Row {
    if (envelope.entity !== 'encounter') return { catalog: Object.assign({}, ...arrivals.map(entity => fromBaseRecord(entity.base.catalog) ? entity.base.catalog : {})) };
    const held = encounterMemberSharedInputs(envelope.base), shared: Row = {}, owned = new WeakSet<object>();
    for (const entity of arrivals) {
        const inputs = encounterMemberSharedInputs(entity.base);
        delete inputs.inventory_order;
        absorbInventoryMetadata(shared, inputs, held, owned);
    }
    return { new_entity_base: shared, catalog: fromBaseRecord(shared.catalog) ? shared.catalog : {} };
}
const location = new Set(['encounter_id','x_pos','y_pos','z_pos','movement_rev','movement_last_operation_id','encounter_creature_t_updated']);
export function relocationColumnsOnly(cols: Row): boolean {
    return Object.keys(cols).every(key => key === 'encounter_creature_id' || location.has(key));
}

export interface RelocationMemberFailure { id: string; reason: string }
export interface RelocationMemberBatch { members: readonly AppliedNewEntity[]; failures: readonly RelocationMemberFailure[] }

/** Relays carry the movers' definition closure too. A changed resident definition still needs its ordinary compile lane. */
export async function relocationDefinitionsUnchanged(base: Row, ops: readonly BasePatchOp[]): Promise<boolean> {
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    return (await Promise.all(ops.filter(op => ['items', 'item_layer_types'].includes(op.table)).map(async op => {
        const definition = catalog[upper(op.row_id)];
        if (!fromBaseRecord(definition)) return !Array.isArray(base.missing_definition_ids)
            || !base.missing_definition_ids.includes(upper(op.row_id));
        if (op.op !== 'upsert' || !await sameTree(definition[op.table], op.cols)) return false;
        return (await Promise.all(Object.entries(op.inputs ?? {}).map(([key, value]) => sameTree(definition[key], value)))).every(Boolean);
    }))).every(Boolean);
}

/** Raw saved columns, including null/zero counters. A simultaneous rules/ownership change still uses its normal lane. */
export function relocationMemberMatches(base: Row, cols: Row): boolean {
    return fromBaseRecord(base.encounter_creatures) && Object.keys(cols).every(key => location.has(key)
        || String(encounterCreatureField(base.encounter_creatures, key) ?? '') === String(encounterCreatureField(cols, key) ?? ''));
}

/** Only creature membership/position plus its delivered definition closure. Never classify a status/inventory/rules update as a move. */
export function relocationArrivalIds(envelope: BaseEnvelope, ops: readonly BasePatchOp[]): string[] | null {
    if (!['encounter','creature','pc'].includes(envelope.entity) || !ops.some(op => op.table === 'encounter_creatures')) return null;
    const members = [envelope.base, ...Array.isArray(envelope.base.encounter_creatures_arr) ? envelope.base.encounter_creatures_arr.filter(fromBaseRecord) : []];
    const incoming: string[] = [];
    for (const op of ops) {
        if (['items','item_layer_types'].includes(op.table) && op.op === 'upsert') continue;
        if (op.table !== 'encounter_creatures') return null;
        if (op.op === 'delete') continue;
        const id = upper(op.row_id), member = members.find(member => idOf(member) === id);
        if (member && !relocationMemberMatches(member, op.cols)) return null;
        // An empty saved destination is a departure, not an arrival to hydrate.
        if (member && envelope.entity !== 'encounter'
            && upper(encounterCreatureField(op.cols, 'encounter_id'))
            && upper(encounterCreatureField(member.encounter_creatures, 'encounter_id')) !== upper(encounterCreatureField(op.cols, 'encounter_id'))) incoming.push(id);
        if (!member) {
            if (envelope.entity !== 'encounter' || upper(encounterCreatureField(op.cols,'encounter_id')) !== upper(envelope.entity_id)) return null;
            incoming.push(id);
        }
    }
    return incoming;
}

/** Build one scoped membership diff. Existing residents and floor objects stay borrowed. */
export function savedCreatureRelocationFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], arrivals: readonly AppliedNewEntity[], computed: unknown,
    unavailable: ReadonlySet<string> = new Set()): AppliedFrame | null {
    const wanted = relocationArrivalIds(envelope, ops);
    if (!wanted) return null;
    const rows: AppliedTouchedRow[] = [], deletes: AppliedFrame['deletes'][number][] = [], member_revs: Record<string, number> = {};
    for (const op of ops) if (op.table === 'encounter_creatures') {
        if (op.op === 'delete') deletes.push({ table: op.table, row_id: op.row_id });
        else {
            if (unavailable.has(upper(op.row_id))) continue;
            // Membership-only saves do not advance movement_rev. Its omission must
            // not become revision zero and discard the departure on a moved creature.
            const savedRevision = encounterCreatureField(op.cols, 'movement_rev');
            if (savedRevision !== null && savedRevision !== undefined) {
                const rev = Number(savedRevision);
                if (!Number.isSafeInteger(rev) || rev < 0) return null;
                member_revs[upper(op.row_id)] = rev;
            }
            // Saved columns belong to the raw base. Replaying them over a compiled mover would
            // replace derived mirrors (Spirit, for example) with stale persisted values.
            rows.push({ table: op.table, row_id: op.row_id, partial: true, base: op.cols,
                row: Object.fromEntries(Object.entries(op.cols).filter(([key]) => location.has(key))) });
        }
    }
    const frame: AppliedFrame = { kind:'applied', operation_id:`creature-relocation:${envelope.entity}:${envelope.entity_id}:${Math.max(...ops.map(op=>op.base_rev))}`,
        encounter_id:envelope.entity_id, actor_id:'', rev:0, member_revs, rows, computed:{}, new_entities:[], deletes };
    const born: AppliedNewEntity[] = [], packages: AppliedNewEntity[] = [];
    const replacements = new Set([...wanted.filter(id => !unavailable.has(id)), ...arrivals.filter(entity => rows.some(row => upper(row.row_id) === upper(entity.entity_id))).map(entity => upper(entity.entity_id))]);
    for (const id of replacements) {
        const arrival = arrivals.find(entity => upper(entity.entity_id) === id), op = ops.find(op => upper(op.row_id) === id && op.table === 'encounter_creatures');
        if (!arrival || !op || !relocationMemberMatches(arrival.base, op.cols)) return null;
        const revision = Number(encounterCreatureField(arrival.base.encounter_creatures,'movement_rev') ?? 0);
        if (revision > member_revs[id]! || revision + 1 < member_revs[id]!) return null;
        const patch = { ...frame, rows: rows.filter(row => upper(row.row_id) === id) };
        // The complete arrival stays page-local; its package is subtracted against the held room once, below.
        packages.push(arrival);
        born.push({ ...arrival, base: compactRelocationArrival(applyAppliedFrame(patch, arrival.base, true, false).value as Row, envelope),
            member: applyAppliedFrame(patch, arrival.member, false, false).value as Row });
    }
    frame.new_entities = born;
    if (born.length) frame.inputs = { relocation_replace: true, ...sharedArrivalInputs(packages, envelope) };
    if (envelope.entity === 'encounter' && fromBaseRecord(computed)) {
        const removed = new Set(deletes.map(row => upper(row.row_id)));
        const roster = (Array.isArray(computed.encounter_creatures_arr) ? computed.encounter_creatures_arr.filter(fromBaseRecord) : [])
            .filter(row => !removed.has(idOf(row)) && !replacements.has(idOf(row)));
        frame.inputs = { ...frame.inputs, relocation_totals: { encounter_id: envelope.entity_id,
            values: compileEncounterRosterTotals([...roster, ...born.map(entity => entity.member)], layerOf) } };
    }
    return frame;
}
